<!-- Wrapper for filter/filter action container -->
<section class="py-1" [ngClass]="isResourceFilterable ? 'mb-3' : 'mb-5'">
  <span class="ctx-filter flex flex-row gap-2 w-full box-border">
    <!-- Active filters -->
    <span
      class="flex-grow flex flex-row gap-x-2 ctx-filter__active-filters flex-wrap items-center overflow-auto"
    >
      @if (resourceTableService.filters.length === 0) {
      <span class="text-xs opacity-60 py-1">
        {{
          isResourceFilterable
            ? 'No filters are active on this table currently. Use the Add Filter button on the right to add one.'
            : 'This resource has no filters.'
        }}
      </span>
      } @else {
      <span class="text-xs opacity-60"
        >Active Filter{{ resourceTableService.filters.length > 1 ? 's' : '' }}:
      </span>
      @for ( activeFilter of resourceTableService.filters; track activeFilter;
      let i = $index; let isLast = $last) {
      <ctx-button-group>
        <button
          ctx-button--secondary
          (click)="openFilterUpdateForm($event, i)"
          size="sm"
          color="primary"
          [title]="
            'Active Filter: ' + getLabelForFilter(activeFilter, dataCache)
          "
        >
          <span class="block max-w-sm overflow-ellipsis overflow-hidden">{{
            getLabelForFilter(activeFilter, dataCache)
          }}</span>
        </button>
        <button
          ctx-button--secondary
          actionIcon="CLEAR"
          color="warn"
          size="sm"
          title="Remove filter"
          (click)="removeFilter(i)"
        ></button>
      </ctx-button-group>
      @if (!isLast) {
      <span class="font-mono text-xs select-none">and</span>
      } } }
    </span>
    <!-- Filter actions -->
    <span class="flex flex-col md:flex-row items-center gap-2">
      @if (isResourceFilterable) {
      <ctx-button-group>
        <button
          ctx-button--secondary
          actionIcon="FILTER"
          (click)="openFilterCreateForm($event)"
          cdkOverlayOrigin
          size="sm"
        >
          Add Filter
        </button>
        @if (resourceTableService.actions.segments) {
        <button
          ctx-button--secondary
          size="sm"
          actionIcon="FILTER_LIST"
          [matMenuTriggerFor]="menu"
        ></button>

        } @if (showSaveSegmentButton && segmentService.segmentsFromApi.length
        !== 100) {
        <button
          ctx-button--secondary
          actionIcon="SAVE"
          color="accent"
          size="sm"
          [matTooltip]="SEGMENT_HELP_TITLE"
          [disabled]="!segmentService.writeAllowed"
          (click)="
            segmentService.launchCreationDialogWithParams(
              resourceTableService.filters
            )
          "
        ></button>
        }
      </ctx-button-group>
      }
      <mat-menu #menu="matMenu">
        @for (segment of segmentService.segments; track $index) {
        <button
          mat-menu-item
          (click)="segmentService.setActiveSegment(segment)"
        >
          {{ segment.name }}
        </button>
        }
      </mat-menu>
    </span>
  </span>
</section>

<!-- Create/Update filter form -->
<ng-template cdkPortal #filterFormTemplate="cdkPortal">
  <form
    (ngSubmit)="submit()"
    [formGroup]="filterForm"
    class="flex flex-col gap-y-4 w-full"
  >
    <!-- Property -->
    <mat-form-field>
      <mat-label>Property</mat-label>
      <input
        autocomplete="off"
        type="text"
        name=""
        matInput
        [matAutocomplete]="autocomplete"
        formControlName="property"
        [readonly]="filterFormStatus.type === CtxFormMode.Update ? true : null"
        #propertyField
      />
    </mat-form-field>
    <mat-autocomplete
      #autocomplete="matAutocomplete"
      [displayWith]="getResourceDisplayName"
      (optionSelected)="updateFilterOperatorList()"
    >
      <!-- MatOption(s) are generated for all the properties that can be filtered. These are defined in filterableProperties and the names are defined in filterablePropertyIds -->
      <!-- This value is then piped to the filterSearch pipe with the value of the control.  -->
      @for ( property of resourceTableService.filterablePropertyIds |
      autocompleteSearch : filterForm.value.property ; track property) {
      <mat-option [value]="property">
        {{ getResourceDisplayName(property) }}
      </mat-option>
      }
    </mat-autocomplete>

    <!-- Comparison -->
    @if (comparisonOperators) {
    <mat-form-field>
      <mat-label>Comparsion</mat-label>
      <mat-select formControlName="operator" required>
        @for (comparisonOperator of comparisonOperators; track
        comparisonOperator) {
        <mat-option value="{{ comparisonOperator }}">
          {{ getLabelForComparisonOperator(comparisonOperator) }}
        </mat-option>
        }
      </mat-select>
    </mat-form-field>
    }

    <!-- The value field is hidden till property && operator field is filled. -->
    @if (filterForm.value.property && filterForm.value.operator) { @switch (
    resourceTableService.filterableProperties[filterForm.value.property] ) {
    <!-- Date field -->
    @case ('objectDate') {
    <mat-form-field>
      <mat-label>Date</mat-label>
      <input
        autocomplete="off"
        formControlName="value"
        matInput
        [matDatepicker]="picker"
        (dateChange)="handleDateSelection($event)"
      />
      <ctx-datepicker-toggle matSuffix [for]="picker"></ctx-datepicker-toggle>
      <mat-datepicker #picker></mat-datepicker>
    </mat-form-field>
    }
    <!-- Number object -->
    @case ('objectNumber') {
    <mat-form-field>
      <mat-label>Number</mat-label>
      <input
        autocomplete="off"
        type="number"
        formControlName="value"
        matInput
      />
    </mat-form-field>
    }
    <!-- Number -->
    @case ('number') {
    <mat-form-field>
      <mat-label>Number</mat-label>
      <input
        autocomplete="off"
        type="number"
        formControlName="value"
        matInput
      />
    </mat-form-field>
    }
    <!-- Boolean -->
    @case ('boolean') {
    <mat-form-field>
      <mat-label>Value</mat-label>
      <mat-select formControlName="value">
        <mat-option value="true">True</mat-option>
        <mat-option value="false">False</mat-option>
      </mat-select>
    </mat-form-field>
    }
    <!-- License Type -->
    @case ('licenseType') {
    <mat-form-field>
      <mat-label>Value</mat-label>
      <mat-select formControlName="value" multiple>
        <mat-option value="node-locked">Node Locked</mat-option>
        <mat-option value="on-premise-floating">On-Premise Floating</mat-option>
        <mat-option value="hosted-floating">Hosted Floating</mat-option>
      </mat-select>
    </mat-form-field>
    }
    <!-- Product -->
    @case ('product') {
    <mat-form-field>
      <mat-label>Product</mat-label>
      <mat-select formControlName="value" multiple>
        @for (product of dataCache.getCachedValues('product'); track product) {
        <mat-option [value]="product.id">{{ product.name }}</mat-option>
        }
      </mat-select>
    </mat-form-field>
    }
    <!-- Products linked to a resource -->
    @case ('products') {
    <mat-form-field>
      <mat-label>Products</mat-label>
      <mat-select formControlName="value" multiple>
        @for (product of dataCache.getCachedValues('product'); track product) {
        <mat-option [value]="product.id">{{ product.name }}</mat-option>
        }
      </mat-select>
    </mat-form-field>
    }
    <!-- Webhook -->
    @case ('webhook') {
    <mat-form-field>
      <mat-label>Webhook</mat-label>
      <mat-select formControlName="value" multiple>
        @for (webhook of dataCache.getCachedValues('webhook'); track webhook) {
        <mat-option class="ph-no-capture" [value]="webhook.id">{{
          webhook.name
        }}</mat-option>
        }
      </mat-select>
    </mat-form-field>
    } @case('usergroup') {
    <mat-form-field>
      <mat-label>User Group</mat-label>
      <mat-select formControlName="value" multiple>
        @for (userGroup of dataCache.getCachedValues('user-group'); track
        userGroup) {
        <mat-option [value]="userGroup.id">{{ userGroup.name }}</mat-option>
        }
      </mat-select>
    </mat-form-field>
    }
    <!-- Automated Email -->
    @case ('automated-email') {
    <mat-form-field>
      <mat-label>Automated Email</mat-label>
      <mat-select formControlName="value" multiple>
        @for ( automatedEmail of dataCache.getCachedValues('automated-email') ;
        track automatedEmail) {
        <mat-option class="ph-no-capture" [value]="automatedEmail.id">{{
          automatedEmail.name
        }}</mat-option>
        }
      </mat-select>
    </mat-form-field>
    }
    <!-- Country -->
    @case ('country') {
    <mat-form-field>
      <mat-label>Country</mat-label>
      <mat-select formControlName="value" multiple>
        @for (country of ALL_COUNTRIES | keyvalue : originalOrder; track
        country) {
        <mat-option [value]="country.key">{{ country.value }}</mat-option>
        }
      </mat-select>
    </mat-form-field>
    }
    <!-- Product Version -->
    @case ('product-version') {
    <mat-form-field>
      <mat-label>Product Version</mat-label>
      <mat-select formControlName="value" multiple>
        @for ( productVersion of dataCache.getCachedValues('product-version') ;
        track productVersion) {
        <mat-option class="ph-no-capture" [value]="productVersion.id">{{
          productVersion.name
        }}</mat-option>
        }
      </mat-select>
    </mat-form-field>
    }
    <!-- Audit Log Action -->
    @case ('action') {
    <mat-form-field>
      <mat-label>Action</mat-label>
      <mat-select formControlName="value" multiple>
        @for ( action of AUDIT_LOG_ACTIONS; track action) {
        <mat-option class="ph-no-capture" [value]="action">{{
          action
        }}</mat-option>
        }
      </mat-select>
    </mat-form-field>
    }
    <!-- User -->
    @case ('user') { @if (searchConfig.userList && searchConfig.userRead) {
    <mat-form-field>
      <ctx-customer-search
        [read]="searchConfig.userRead"
        [list]="searchConfig.userList"
        formControlName="value"
        label="User"
        type="user"
        (userName)="setDisplayValue($event)"
      ></ctx-customer-search>
    </mat-form-field>
    } }
    <!-- Organization -->
    @case ('organization') { @if (searchConfig.orgList && searchConfig.orgRead)
    {
    <mat-form-field>
      <ctx-customer-search
        type="organization"
        formControlName="value"
        [read]="searchConfig.orgRead"
        [list]="searchConfig.orgList"
        label="Organization"
        (userName)="setDisplayValue($event)"
      ></ctx-customer-search>
    </mat-form-field>
    } }
    <!-- Reseller -->
    @case ('reseller') { @if (searchConfig.resellerList &&
    searchConfig.resellerRead) {
    <mat-form-field>
      <ctx-customer-search
        type="reseller"
        formControlName="value"
        [read]="searchConfig.resellerRead"
        [list]="searchConfig.resellerList"
        label="Reseller"
        (userName)="setDisplayValue($event)"
      ></ctx-customer-search>
    </mat-form-field>
    } }
    <!-- Tag -->
    @case ('tag') {
    <mat-form-field>
      <mat-label>Tag</mat-label>
      <mat-select formControlName="value" multiple>
        @for (tag of dataCache.getCachedValues('tag'); track tag) {
        <mat-option class="ph-no-capture" [value]="tag.name">{{
          tag.name
        }}</mat-option>
        }
      </mat-select>
    </mat-form-field>
    }
    <!-- Resource -->
    @case ('resource') {
    <mat-form-field class="mat-mdc-form-field--small">
      <mat-label>Resource</mat-label>
      <mat-select formControlName="value" multiple>
        @for (resource of dataCache.getCachedValues('audit-log-resource'); track
        resource) {
        <mat-option class="ph-no-capture" [value]="resource">{{
          getResourceDisplayName(resource)
        }}</mat-option>
        }
      </mat-select>
    </mat-form-field>
    }
    <!-- ResourceId -->
    @case ('resourceId') {
    <mat-form-field class="mat-mdc-form-field--small">
      <mat-label>Resource ID</mat-label>
      <input autocomplete="off" type="text" formControlName="value" matInput />
      @if ( filterForm.get('operator')?.value === 'in' ||
      filterForm.get('operator')?.value === 'nin' ) {
      <mat-hint>Separate multiple values with comma</mat-hint>
      }
    </mat-form-field>
    }
    <!-- Role -->
    @case ('role') {
    <mat-form-field>
      <mat-label>Role</mat-label>
      <mat-select formControlName="value" multiple>
        <mat-option value="user">User</mat-option>
        <mat-option value="organization-admin">Organization Admin</mat-option>
        <mat-option value="reseller-admin">Reseller Admin</mat-option>
      </mat-select>
    </mat-form-field>
    }
    <!-- OS -->
    @case ('os') {
    <mat-form-field>
      <mat-label>OS</mat-label>
      <mat-select formControlName="value" multiple>
        @for (os of ALL_OS | keyvalue : originalOrder; track os) {
        <mat-option [value]="os.key">{{ os.value }}</mat-option>
        }
      </mat-select>
    </mat-form-field>
    }
    <!-- Default -->
    @default {
    <mat-form-field>
      <mat-label>Value</mat-label>
      <input autocomplete="off" type="text" formControlName="value" matInput />
      @if ( filterForm.get('operator')?.value === 'in' ||
      filterForm.get('operator')?.value === 'nin' ) {
      <mat-hint>Separate multiple values with comma</mat-hint>
      }
    </mat-form-field>
    } } }

    <!-- Action buttons -->
    <section class="flex flex-row items-center gap-4 justify-end">
      <button ctx-button--secondary type="button" (click)="overlayRef.detach()">
        Cancel
      </button>
      <button ctx-button--primary type="submit" title="Apply Filter">
        Apply Filter
      </button>
    </section>
  </form>
</ng-template>
